import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import Load from '~/components/Load';
import Paginator from '~/components/Paginator';
import ApiErrorComponent from '~/partials/ApiErrorComponent';
import { ListTabSwitch } from '~/partials/TabSwitch';
import { LevelTags, TagsBase } from '~/partials/Tags';
import { ScrollArea } from '~/primitives/scroll-area';
import { useList } from '~/util/hooks/useList';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import { changelogQuery } from '~/util/queries/public/other';
import { ChangelogEntry } from '~/util/types/changelog';
import { BaseLevel } from '~/util/types/levels';

const ChangelogLevelTags = (props: TagsBase<BaseLevel>) => {
    return (
        <LevelTags
            className="bg-transparent text-secondary-foreground"
            {...props}
        />
    );
};

function ChangelogContent() {
    const [page, setPage] = useState(1);
    const list = useList();
    const { data, isError, error } = useSuspenseQuery(
        changelogQuery({ list, page }),
    );
    const localeDateString = useLocaleDateString();

    const getAboveBelow = (entry: ChangelogEntry) => {
        return (
            <>
                {entry.level_above || entry.level_below ? ', ' : ''}
                {entry.level_below ? (
                    <>
                        above <ChangelogLevelTags item={entry.level_below} />{' '}
                        {entry.level_above ? 'and ' : ''}
                    </>
                ) : (
                    ''
                )}
                {entry.level_above ? (
                    <>
                        below <ChangelogLevelTags item={entry.level_above} />
                    </>
                ) : (
                    ''
                )}
            </>
        );
    };

    const getChangelogMessage = (entry: ChangelogEntry) => {
        if (entry.action == 'Pending') {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> was added
                    as pending
                </>
            );
        } else if ('Placed' in entry.action) {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> was
                    placed at <span>#{entry.action.Placed.new_position}</span>
                    {getAboveBelow(entry)}
                </>
            );
        } else if ('Raised' in entry.action) {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> was
                    raised from <span>#{entry.action.Raised.old_position}</span>{' '}
                    to <span>#{entry.action.Raised.new_position}</span>
                    {getAboveBelow(entry)}
                </>
            );
        } else if ('Lowered' in entry.action) {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> was
                    lowered from{' '}
                    <span>#{entry.action.Lowered.old_position}</span> to{' '}
                    <span>#{entry.action.Lowered.new_position}</span>
                    {getAboveBelow(entry)}
                </>
            );
        } else if ('Removed' in entry.action) {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> was
                    removed from the list
                </>
            );
        } else if ('Swapped' in entry.action) {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> and{' '}
                    <ChangelogLevelTags
                        item={entry.action.Swapped.other_level}
                    />{' '}
                    have been swapped, with{' '}
                    <ChangelogLevelTags
                        item={entry.action.Swapped.upper_level}
                    />{' '}
                    now sitting above at{' '}
                    <span>#{entry.action.Swapped.upper_position}</span>
                </>
            );
        } else if ('MovedToLegacy' in entry.action) {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> was moved
                    from <span>#{entry.action.MovedToLegacy.old_position}</span>{' '}
                    to <span>the legacy list</span>
                </>
            );
        } else if ('MovedFromLegacy' in entry.action) {
            return (
                <>
                    <ChangelogLevelTags item={entry.affected_level} /> was moved
                    from <span>the legacy list</span> to{' '}
                    <span>#{entry.action.MovedFromLegacy.new_position}</span>
                    {getAboveBelow(entry)}
                </>
            );
        }
        return <div>Unknown action</div>;
    };

    const groupEntriesByDate = (entries: ChangelogEntry[]) => {
        const acc = entries.reduce<Record<string, ChangelogEntry[]>>(
            (acc, entry) => {
                const date = new Date(entry.created_at);
                const key = date.toISOString().slice(0, 10);

                if (!acc[key]) acc[key] = [];
                acc[key].push(entry);

                return acc;
            },
            {},
        );

        return Object.entries(acc)
            .sort((a, b) => (a[0] < b[0] ? 1 : -1))
            .map(([key, entries]) => ({ key, entries }));
    };

    const groups = groupEntriesByDate(data.data);

    if (isError)
        return error instanceof Error ? <ApiErrorComponent {...error} /> : '';

    return (
        <>
            {groups.map(({ entries, key }) => (
                <div key={key} className="mb-4 text-justify">
                    <h3 className="font-bold text-primary mb-2">
                        {localeDateString(key)}
                    </h3>
                    <ul className="pl-4 mr-8 list-disc">
                        {entries.map((entry) => (
                            <li
                                key={`${entry.created_at}-${entry.affected_level.id}`}
                                className="text-secondary-light [&_span]:text-white my-1"
                            >
                                {getChangelogMessage(entry)}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
            <Paginator
                currentPage={page}
                totalPages={data.pages}
                onPageChange={setPage}
            />
        </>
    );
}

function Changelog() {
    return (
        <>
            <div className="flex flex-col lg:flex-row items-center gap-2 mb-4">
                <h2 className="text-2xl font-bold ">CHANGELOG</h2>
                <ListTabSwitch from="/" className="my-2" size="md" />
            </div>
            <div>
                <ScrollArea className="w-full min-h-[30vh] max-h-[30vh] h-[30vh] my-4">
                    <Suspense fallback={<Load />}>
                        <ChangelogContent />
                    </Suspense>
                </ScrollArea>
            </div>
        </>
    );
}

export default Changelog;
